import dayjs, { Dayjs } from 'dayjs';
import type { CalendarEvent, DayCell, MonthData } from './types';

// Initialize dayjs with required plugins
import isoWeek from 'dayjs/plugin/isoWeek';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';

dayjs.extend(isoWeek);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

/**
 * Generate calendar grid for a given month
 */
export const generateMonthGrid = (
  date: Dayjs,
  selectedDate: Dayjs | null,
  events: CalendarEvent[]
): DayCell[] => {
  const startOfMonth = date.startOf('month');
  const endOfMonth = date.endOf('month');
  const startOfWeek = startOfMonth.startOf('week'); // Sunday
  const endOfWeek = endOfMonth.endOf('week'); // Saturday
  
  const days: DayCell[] = [];
  let current = startOfWeek;
  
  while (current.isSameOrBefore(endOfWeek)) {
    const dateString = current.format('YYYY-MM-DD');
    const dayEvents = events.filter(event => event.date === dateString);
    
    days.push({
      date: current,
      isCurrentMonth: current.month() === date.month(),
      isToday: current.isSame(dayjs(), 'day'),
      isSelected: selectedDate ? current.isSame(selectedDate, 'day') : false,
      events: dayEvents,
    });
    
    current = current.add(1, 'day');
  }
  
  return days;
};

/**
 * Generate year overview with 12 mini calendars
 */
export const generateYearData = (
  year: number,
  selectedDate: Dayjs | null,
  events: CalendarEvent[]
): MonthData[] => {
  const months: MonthData[] = [];
  
  for (let month = 0; month < 12; month++) {
    const monthDate = dayjs().year(year).month(month);
    const days = generateMonthGrid(monthDate, selectedDate, events);
    
    months.push({
      month,
      year,
      name: monthDate.format('MMMM'),
      days,
    });
  }
  
  return months;
};

/**
 * Get events for a specific date range
 */
export const getEventsInRange = (
  events: CalendarEvent[],
  startDate: Dayjs,
  endDate: Dayjs
): CalendarEvent[] => {
  return events.filter(event => {
    const eventDate = dayjs(event.date);
    return eventDate.isSameOrAfter(startDate, 'day') && 
           eventDate.isSameOrBefore(endDate, 'day');
  });
};

/**
 * Group events by date for schedule view
 */
export const groupEventsByDate = (events: CalendarEvent[]): Record<string, CalendarEvent[]> => {
  return events.reduce((groups, event) => {
    const dateKey = event.date;
    if (!groups[dateKey]) {
      groups[dateKey] = [];
    }
    groups[dateKey].push(event);
    return groups;
  }, {} as Record<string, CalendarEvent[]>);
};

/**
 * Sort events by time (all-day events first, then by time)
 */
export const sortEventsByTime = (events: CalendarEvent[]): CalendarEvent[] => {
  return [...events].sort((a, b) => {
    // All-day events come first
    if (a.isAllDay && !b.isAllDay) return -1;
    if (!a.isAllDay && b.isAllDay) return 1;
    
    // Both all-day or both have time
    if (a.time && b.time) {
      return a.time.localeCompare(b.time);
    }
    
    // Sort by title if no time difference
    return a.title.localeCompare(b.title);
  });
};

/**
 * Get formatted header text based on view and date
 */
export const getHeaderText = (date: Dayjs, view: 'month' | 'year' | 'schedule'): string => {
  switch (view) {
    case 'month':
      return date.format('MMMM YYYY');
    case 'year':
      return date.format('YYYY');
    case 'schedule':
      const startOfYear = date.startOf('year');
      const endOfYear = date.endOf('year');
      return `${startOfYear.format('MMM YYYY')}  ${endOfYear.format('MMM YYYY')}`;
    default:
      return date.format('MMMM YYYY');
  }
};

/**
 * Get navigation increment for view
 */
export const getNavigationIncrement = (view: 'month' | 'year' | 'schedule'): { unit: dayjs.ManipulateType; amount: number } => {
  switch (view) {
    case 'month':
      return { unit: 'month', amount: 1 };
    case 'year':
    case 'schedule':
      return { unit: 'year', amount: 1 };
    default:
      return { unit: 'month', amount: 1 };
  }
};

/**
 * Format date for display
 */
export const formatDateForDisplay = (date: Dayjs, format?: string): string => {
  return date.format(format || 'MMM D, YYYY');
};

/**
 * Check if date is in current month
 */
export const isCurrentMonth = (date: Dayjs, referenceDate: Dayjs): boolean => {
  return date.month() === referenceDate.month() && date.year() === referenceDate.year();
};

/**
 * Get color for event (default if not provided)
 */
export const getEventColor = (event: CalendarEvent): string => {
  return event.color || '#1976d2'; // Default Material-UI primary color
};

/**
 * Truncate event title for display
 */
export const truncateEventTitle = (title: string, maxLength: number = 20): string => {
  return title.length > maxLength ? `${title.substring(0, maxLength)}...` : title;
};